import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { getLocationSearch, subscribeToLocationChange, updateLocationParams } from '../webUtils/locationStore';

type QueryParamSelector<T> = (params: URLSearchParams) => T;
type QueryParamUpdater<T> = (currentParams: URLSearchParams, val: T) => URLSearchParams;

/**
 * A hook for interacting with global search params in the url.
 * These params should be identifying params sent by the server.
 * For params concerning UI state, use `useWidgetUrlParams` to interact,
 *
 * Example
 * ```
 * const selector = (url) => ({ contentKey: url.get('contentKey') });
 * const updater = (urlParams, newValues) => {
 *   urlParams.set('someKey', newValues.someVal);
 *   if(newValues.someFlag) {
 *     urlParams.set('otherKey', newValues.a + newValues.b);
 *   }
 *   return urlParams;
 * }
 * const Component = () => {
 *   const [params, updateParams] = useGlobalUrlParams(selector, updater);
 *   // ... some more code
 *   const someAction = () => {
 *     updateParams(someVal);
 *   };
 *   // ... rest of the code
 * }
 * ```
 *
 * @param selector a selector function to extract the specific params that a particular usage is interested in.
 *  The component containing this will only rerender if these specific params change.
 *  NOTE: this selector function should most definitely be defined outside of a component function.
 * @param updater an updater function that, given the existing `URLSearchParams` object and some new value,
 *  would return a `URLSearchParams` object with updated values
 * @returns a tuple of [value, setter] much like what is returned from `useState()`.
 */
export const useGlobalUrlParams = <T, U = T>(
  selector: QueryParamSelector<T>,
  updater?: QueryParamUpdater<U>
): [T, (val: U) => void] => {
  const getSnapshot = useMemo(() => {
    let cachedSearch: string | null = null;
    let cachedResult: T | undefined;
    let hasCache = false;

    return (): T => {
      const search = getLocationSearch();
      if (hasCache && search === cachedSearch) {
        return cachedResult as T;
      }
      cachedSearch = search;
      const params = new URLSearchParams(search);
      const paramEntries: [string, string][] = [];
      params.forEach((value, key) => {
        paramEntries.push([key, value]);
      });
      const lowerCaseParams = paramEntries.map(
        ([key, value]) => [key.toLowerCase(), value] as [string, string]
      );
      const allParams = new URLSearchParams([...paramEntries, ...lowerCaseParams]);
      cachedResult = selector(allParams);
      hasCache = true;
      return cachedResult;
    };
  }, [selector]);

  const params = useSyncExternalStore(subscribeToLocationChange, getSnapshot);
  const updateParams = useCallback(
    (updateVal: U) => {
      if (!updater) {
        return;
      }
      const newParmas = updater(new URLSearchParams(getLocationSearch()), updateVal);
      updateLocationParams(newParmas);
    },
    [updater]
  );

  return [params, updateParams];
};
